'use client';

import type { ReactNode } from 'react';

export type AppToastVariant = 'default' | 'success' | 'info' | 'warning' | 'destructive';

export interface AppToastOptions {
  title?: ReactNode;
  description?: ReactNode;
  /**
   * Visual variant for the toast surface.
   * - default: neutral information
   * - success: positive outcome
   * - info: informational notice
   * - warning: non-blocking warning
   * - destructive: error / failure
   */
  variant?: AppToastVariant;
  /**
   * Duration in milliseconds before the toast auto-dismisses.
   * Defaults to 4000ms when omitted.
   */
  duration?: number;
}

type ToastHandler = (options: AppToastOptions) => void;

export class AppToastService {
  private static handler: ToastHandler | null = null;
  private static lastKey: string | null = null;
  private static lastTimestamp = 0;

  /**
   * Register the concrete toast implementation. This is called once
   * from the ToastProvider mounted near the root of the app.
   */
  static register(handler: ToastHandler): void {
    AppToastService.handler = handler;
  }

  /**
   * Unregister the current toast handler, typically when the provider unmounts.
   */
  static unregister(handler: ToastHandler): void {
    if (AppToastService.handler === handler) {
      AppToastService.handler = null;
    }
  }

  /**
   * Trigger a toast with the provided options. If no provider is mounted yet,
   * the call is ignored but a warning is logged in development.
   */
  static show(options: AppToastOptions): void {
    const now = typeof Date !== 'undefined' ? Date.now() : 0;
    const key = [
      typeof options.title === 'string' ? options.title : '',
      typeof options.description === 'string' ? options.description : '',
      options.variant ?? 'default',
    ].join('::');

    // Deduplicate identical toasts fired in very quick succession, which can
    // happen in React Strict Mode when effects are invoked twice in dev.
    if (AppToastService.lastKey === key && now - AppToastService.lastTimestamp < 500) {
      return;
    }
    AppToastService.lastKey = key;
    AppToastService.lastTimestamp = now;

    if (AppToastService.handler) {
      AppToastService.handler(options);
      return;
    }
    if (typeof window !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn('[AppToastService] No toast handler registered. Make sure ToastProvider is mounted.');
    }
  }

  static success(description: ReactNode, title: ReactNode = 'Success'): void {
    AppToastService.show({ title, description, variant: 'success' });
  }

  static info(description: ReactNode, title: ReactNode = 'Info'): void {
    AppToastService.show({ title, description, variant: 'info' });
  }

  static warning(description: ReactNode, title: ReactNode = 'Warning'): void {
    AppToastService.show({ title, description, variant: 'warning' });
  }

  static error(description: ReactNode, title: ReactNode = 'Error'): void {
    AppToastService.show({ title, description, variant: 'destructive' });
  }
}
